/**
 * Division-page theme visuals
 *
 * The Science / Engineering / Math / Coding / Robotics division pages used to
 * hardcode their entire theme catalog (title, description, tags, activities,
 * kit). That content has since moved into the Course model (description,
 * topic_tags, sample_activities, kit_items) — see accounts/migrations 0024 &
 * 0025 + seed_summer_2026.py.
 *
 * What still lives in code is purely presentational: the icon component and
 * the gradient/colorAccent classes that give each theme its visual identity.
 * Keyed by course *title* (which matches the DB `Course.title`).
 *
 * Usage:
 *   import { getThemeVisuals } from '.../divisionThemeVisuals';
 *   const v = getThemeVisuals(course.title, course.category, course.id);
 *   // v = { icon: React.Component, color, colorAccent, bgAccent }
 *
 * If a title isn't in the registry, a per-category default is returned so
 * unseeded courses still render cleanly.
 */
import {
  Search, Activity, FlaskConical, Bug, Zap, Telescope, Dna,
  Building2, Wind, Rocket, Layers, Link2, Sun, Home as HomeIcon, Printer,
  Trophy, KeyRound, Shapes, Dice5, Lock, TreeDeciduous, TrendingUp, Brain,
  Code, Smartphone, BookOpen, Monitor, Shield, Film, Database,
  Cpu, Bot, Recycle, Zap as Lightning, LifeBuoy, Swords, Sparkles,
} from 'lucide-react';

/* Category → icon (used when there's no per-title override) */
const CATEGORY_ICONS = {
  science:     FlaskConical,
  technology:  Code,
  coding:      Code,
  engineering: Building2,
  math:        Trophy,
  robotics:    Cpu,
};

/* Category → default visual style (kept as the no-id fallback) */
const CATEGORY_DEFAULTS = {
  science:     { icon: FlaskConical, color: 'from-rose-500 to-pink-400',    colorAccent: 'text-rose-600',    bgAccent: 'bg-rose-50 border-rose-200' },
  technology:  { icon: Code,         color: 'from-emerald-500 to-teal-400', colorAccent: 'text-emerald-600', bgAccent: 'bg-emerald-50 border-emerald-200' },
  coding:      { icon: Code,         color: 'from-emerald-500 to-teal-400', colorAccent: 'text-emerald-600', bgAccent: 'bg-emerald-50 border-emerald-200' },
  engineering: { icon: Building2,    color: 'from-amber-500 to-orange-400', colorAccent: 'text-amber-600',   bgAccent: 'bg-amber-50 border-amber-200' },
  math:        { icon: Trophy,       color: 'from-sky-500 to-blue-400',     colorAccent: 'text-sky-600',     bgAccent: 'bg-sky-50 border-sky-200' },
  robotics:    { icon: Cpu,          color: 'from-purple-500 to-violet-400', colorAccent: 'text-purple-600', bgAccent: 'bg-purple-50 border-purple-200' },
};

/*
 * Curated palette of 12 harmonious gradient combos.
 * Spread across the hue wheel so adjacent cards on a page
 * always look distinct but cohesive.
 */
const COLOR_PALETTE = [
  { color: 'from-rose-500 to-pink-400',     colorAccent: 'text-rose-600',     bgAccent: 'bg-rose-50 border-rose-200' },
  { color: 'from-orange-500 to-red-400',    colorAccent: 'text-orange-600',   bgAccent: 'bg-orange-50 border-orange-200' },
  { color: 'from-amber-500 to-yellow-400',  colorAccent: 'text-amber-600',    bgAccent: 'bg-amber-50 border-amber-200' },
  { color: 'from-green-500 to-emerald-400', colorAccent: 'text-green-600',    bgAccent: 'bg-green-50 border-green-200' },
  { color: 'from-emerald-500 to-teal-400',  colorAccent: 'text-emerald-600',  bgAccent: 'bg-emerald-50 border-emerald-200' },
  { color: 'from-teal-500 to-cyan-400',     colorAccent: 'text-teal-600',     bgAccent: 'bg-teal-50 border-teal-200' },
  { color: 'from-sky-500 to-blue-400',      colorAccent: 'text-sky-600',      bgAccent: 'bg-sky-50 border-sky-200' },
  { color: 'from-blue-500 to-indigo-400',   colorAccent: 'text-blue-600',     bgAccent: 'bg-blue-50 border-blue-200' },
  { color: 'from-indigo-500 to-violet-400', colorAccent: 'text-indigo-600',   bgAccent: 'bg-indigo-50 border-indigo-200' },
  { color: 'from-violet-500 to-purple-400', colorAccent: 'text-violet-600',   bgAccent: 'bg-violet-50 border-violet-200' },
  { color: 'from-purple-500 to-fuchsia-400',colorAccent: 'text-purple-600',   bgAccent: 'bg-purple-50 border-purple-200' },
  { color: 'from-fuchsia-500 to-pink-400',  colorAccent: 'text-fuchsia-600',  bgAccent: 'bg-fuchsia-50 border-fuchsia-200' },
];

/* Stable hash of a course UUID → palette index */
function hashId(id) {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) {
    h = (Math.imul(31, h) + id.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/* Per-title visual overrides (harvested from the legacy division pages). */
const THEME_VISUALS = {
  // ───── Science
  'Junior Doctors: Medical Mystery Lab':       { icon: Search,       color: 'from-rose-500 to-pink-400',     colorAccent: 'text-rose-600',   bgAccent: 'bg-rose-50 border-rose-200' },
  'The Amazing Human Machine':                 { icon: Activity,     color: 'from-red-500 to-orange-400',    colorAccent: 'text-red-600',    bgAccent: 'bg-red-50 border-red-200' },
  'Fizz, Pop, Boom! Chemistry Lab':            { icon: FlaskConical, color: 'from-purple-500 to-violet-400', colorAccent: 'text-purple-600', bgAccent: 'bg-purple-50 border-purple-200' },
  'Blast Off! Space Explorers Academy':        { icon: Rocket,       color: 'from-green-500 to-emerald-400', colorAccent: 'text-green-600',  bgAccent: 'bg-green-50 border-green-200' },
  'Tiny World, Big Discoveries: Microscope Lab': { icon: Telescope,  color: 'from-cyan-500 to-teal-400',     colorAccent: 'text-cyan-600',   bgAccent: 'bg-cyan-50 border-cyan-200' },
  'Weather Warriors: Storm Science Lab':       { icon: Zap,          color: 'from-indigo-500 to-blue-400',   colorAccent: 'text-indigo-600', bgAccent: 'bg-indigo-50 border-indigo-200' },
  'Heartbeat Hackers: Build Your Own Health Monitor': { icon: Dna,   color: 'from-sky-500 to-blue-400',      colorAccent: 'text-sky-600',    bgAccent: 'bg-sky-50 border-sky-200' },
  'AI for Medicine: Train a Disease-Detecting Model': { icon: Sparkles, color: 'from-fuchsia-500 to-pink-400', colorAccent: 'text-fuchsia-600', bgAccent: 'bg-fuchsia-50 border-fuchsia-200' },

  // ───── Engineering
  'Bridge Builders: Engineering Showdown':     { icon: Building2,    color: 'from-cyan-500 to-teal-400',     colorAccent: 'text-cyan-600',   bgAccent: 'bg-cyan-50 border-cyan-200' },
  'Wind Wizards: Turbine Power Lab':           { icon: Wind,         color: 'from-sky-500 to-indigo-400',    colorAccent: 'text-sky-600',    bgAccent: 'bg-sky-50 border-sky-200' },
  'Launch Lab: Catapult Engineering Challenge': { icon: Rocket,      color: 'from-orange-500 to-red-400',    colorAccent: 'text-orange-600', bgAccent: 'bg-orange-500/15 border-orange-400/20' },
  'Sky High! Tower Engineering Challenge':     { icon: Layers,       color: 'from-amber-500 to-yellow-400',  colorAccent: 'text-amber-600',  bgAccent: 'bg-amber-50 border-amber-200' },
  'Chain Reaction! Rube Goldberg Machines':    { icon: Link2,        color: 'from-violet-500 to-purple-400', colorAccent: 'text-violet-600', bgAccent: 'bg-violet-500/15 border-violet-400/20' },
  'Sun-Powered Speedsters: Solar Car Lab':     { icon: Sun,          color: 'from-yellow-500 to-orange-400', colorAccent: 'text-yellow-600', bgAccent: 'bg-yellow-500/15 border-yellow-400/20' },
  'Dream Builders: Cardboard Architecture Studio': { icon: HomeIcon, color: 'from-teal-500 to-cyan-400',     colorAccent: 'text-teal-600',   bgAccent: 'bg-teal-500/15 border-teal-400/20' },
  '3D Print Lab: From Idea to Reality':        { icon: Printer,      color: 'from-pink-500 to-rose-400',     colorAccent: 'text-pink-600',   bgAccent: 'bg-pink-500/15 border-pink-400/20' },

  // ───── Math
  'Math Olympics: Team Problem-Solving Showdown': { icon: Trophy,    color: 'from-amber-500 to-yellow-400',  colorAccent: 'text-amber-600',  bgAccent: 'bg-amber-50 border-amber-200' },
  'Secret Codes: Spy Math Academy':            { icon: KeyRound,     color: 'from-slate-500 to-gray-400',    colorAccent: 'text-slate-600',  bgAccent: 'bg-slate-500/15 border-slate-400/20' },
  'Geometry Art Lab: Shapes, Symmetry & Design': { icon: Shapes,     color: 'from-fuchsia-500 to-pink-400',  colorAccent: 'text-fuchsia-600', bgAccent: 'bg-fuchsia-50 border-fuchsia-200' },
  'Roll the Dice! Probability Playground':     { icon: Dice5,        color: 'from-red-500 to-rose-400',      colorAccent: 'text-red-600',    bgAccent: 'bg-red-50 border-red-200' },
  'Escape the Room: Math Puzzle Challenge':    { icon: Lock,         color: 'from-indigo-500 to-blue-400',   colorAccent: 'text-indigo-600', bgAccent: 'bg-indigo-50 border-indigo-200' },
  "Nature's Hidden Math: Fractals & Patterns": { icon: TreeDeciduous, color: 'from-green-500 to-emerald-400', colorAccent: 'text-green-600',  bgAccent: 'bg-green-50 border-green-200' },
  'Boss Mode: Business & Money Math':          { icon: TrendingUp,   color: 'from-emerald-500 to-teal-400',  colorAccent: 'text-emerald-600', bgAccent: 'bg-emerald-50 border-emerald-200' },
  'Brain Games: Logic & Strategy Lab':         { icon: Brain,        color: 'from-purple-500 to-violet-400', colorAccent: 'text-purple-600', bgAccent: 'bg-purple-50 border-purple-200' },

  // ───── Technology / Coding
  'Game On! Build Your First Video Game':      { icon: Code,         color: 'from-emerald-500 to-teal-400',  colorAccent: 'text-emerald-600', bgAccent: 'bg-emerald-50 border-emerald-200' },
  'Quest Coder: Build a Python Adventure Game': { icon: BookOpen,    color: 'from-yellow-500 to-amber-400',  colorAccent: 'text-yellow-600', bgAccent: 'bg-yellow-500/15 border-yellow-400/20' },
  'Web Wizards: Design & Build Your Own Website': { icon: Monitor,   color: 'from-blue-500 to-cyan-400',     colorAccent: 'text-blue-600',   bgAccent: 'bg-blue-500/15 border-blue-400/20' },
  'Glow Lab: Build a Color-Changing Moon Lamp': { icon: Sparkles,    color: 'from-violet-500 to-fuchsia-400', colorAccent: 'text-violet-600', bgAccent: 'bg-violet-500/15 border-violet-400/20' },
  'Cyber Squad: Digital Safety Mission':       { icon: Shield,       color: 'from-red-500 to-rose-400',      colorAccent: 'text-red-600',    bgAccent: 'bg-red-50 border-red-200' },
  'App Sharks: Design Your Own Mobile App':    { icon: Smartphone,   color: 'from-sky-500 to-blue-400',      colorAccent: 'text-sky-600',    bgAccent: 'bg-sky-50 border-sky-200' },
  'Animation Station: Bring Your Characters to Life': { icon: Film,  color: 'from-pink-500 to-rose-400',     colorAccent: 'text-pink-600',   bgAccent: 'bg-pink-500/15 border-pink-400/20' },
  'Data Detectives: Crack the Code with Real Data': { icon: Database, color: 'from-indigo-500 to-purple-400', colorAccent: 'text-indigo-600', bgAccent: 'bg-indigo-50 border-indigo-200' },

  // ───── Robotics
  'Glow-Botics: Build and Code a Smart RGB Lamp': { icon: Cpu,       color: 'from-rose-500 to-pink-400',     colorAccent: 'text-rose-600',   bgAccent: 'bg-rose-50 border-rose-200' },
  'Kinetic Creators: The Responsive Toy Lab':  { icon: Bot,          color: 'from-orange-500 to-amber-400',  colorAccent: 'text-orange-600', bgAccent: 'bg-orange-500/15 border-orange-400/20' },
  'Eco-Bot: Build a Smart Recycling Robot':    { icon: Recycle,      color: 'from-green-500 to-emerald-400', colorAccent: 'text-green-600',  bgAccent: 'bg-green-50 border-green-200' },
  'LEGO Robotics: Build, Code & Challenge':    { icon: Bot,          color: 'from-yellow-500 to-orange-400', colorAccent: 'text-yellow-600', bgAccent: 'bg-yellow-500/15 border-yellow-400/20' },
  'Light It Up! LED Pixel Art Studio':         { icon: Lightning,    color: 'from-cyan-500 to-blue-400',     colorAccent: 'text-cyan-600',   bgAccent: 'bg-cyan-50 border-cyan-200' },
  'Rescue Bot: Obstacle Course Mission':       { icon: LifeBuoy,     color: 'from-teal-500 to-emerald-400',  colorAccent: 'text-teal-600',   bgAccent: 'bg-teal-500/15 border-teal-400/20' },
  'Battle Bots: Design, Build & Compete':      { icon: Swords,       color: 'from-red-500 to-rose-400',      colorAccent: 'text-red-600',    bgAccent: 'bg-red-50 border-red-200' },
  'AI Robots: Smart Mission Lab':              { icon: Bug,          color: 'from-purple-500 to-violet-400', colorAccent: 'text-purple-600', bgAccent: 'bg-purple-50 border-purple-200' },
};

export function getThemeVisuals(title, category, id) {
  if (THEME_VISUALS[title]) return THEME_VISUALS[title];
  const cat = (category || '').toLowerCase();
  if (id) {
    const colors = COLOR_PALETTE[hashId(id) % COLOR_PALETTE.length];
    const icon = CATEGORY_ICONS[cat] || FlaskConical;
    return { icon, ...colors };
  }
  return CATEGORY_DEFAULTS[cat] || CATEGORY_DEFAULTS.science;
}

/**
 * Adapt a raw API course into the shape the division-page renderers
 * historically expected (so we don't have to rewrite the render code).
 */
export function courseToTheme(course) {
  const v = getThemeVisuals(course.title, course.category, course.id);
  const grades = (course.grade_bands || [])
    .map(g => typeof g === 'string' ? g.replace(/-/g, '–') : g)
    .join(', ');
  // Pick a sensible tagline (first 40 chars of description if no dedicated
  // tagline field exists in the DB).
  const tagline = (course.description || '').split('.')[0].slice(0, 60).trim();
  return {
    id: course.id,
    theme: course.title,
    tagline,
    description: course.description || '',
    grades: grades ? `Grades ${grades}` : '',
    tags: course.topic_tags || [],
    sampleActivities: course.sample_activities || [],
    kitItems: course.kit_items || [],
    // Backwards-compat alias for pages that originally rendered `cat.highlights`
    highlights: course.sample_activities || [],
    thumbnailUrl: course.thumbnail_url || '',
    ...v, // icon, color, colorAccent, bgAccent
  };
}
